import {Scene, Mesh, Vector3, Quaternion, Box3, Object3D, Matrix4, BufferAttribute} from 'three';
import { OBB } from 'three/addons/math/OBB.js';
import Product from '../model/Product';
import { ElementTypeEnum } from '../elementTypeMappings';
import { findFromProducts } from './productModelUtils';
import { isEqualWithinTolerance } from './mathUtils';
import { isProductMeshUserData } from '../interfaces/ProductMeshUserData';

const findProductFromSceneByGlobalId = (scene: Scene, globalId: string): Object3D | null => {
    return findProductsFromSceneByGlobalIds(scene, [globalId])[0] || null;
}

const findProductsFromSceneByGlobalIds = (scene: Scene, globalIds: string[]): Object3D[] => {

    const results: Object3D[] = [];
    scene.traverse((child) => {
        if (isProductMeshUserData(child.userData)) {
            if (globalIds.some(globalId => globalId === child.userData.product.globalId)) {
                results.push(child);
            }
        }
    });

    return results;
}

const findBoundingBoxByProductType = (scene: Scene | null, rootProducts: Product[], types: ElementTypeEnum[]): Box3 => {

    const bbox = new Box3();
    if (scene === null) 
        return bbox;

    const products = findFromProducts(rootProducts, types);

    const productObjects = findProductsFromSceneByGlobalIds(scene, products.map(p => p.globalId));

    productObjects.forEach(productObject => {
                bbox.expandByObject(productObject);
            });

    return bbox;
}

const isBox3Infinite = (box: Box3 | null) => {
    
    if (box === null) 
        return false;

    // Check if any of the min or max components are Infinity or -Infinity
    const { min, max } = box;

    const result = (
      Math.abs(min.x) === -Infinity || Math.abs(min.y) === -Infinity || Math.abs(min.z) === -Infinity ||
      Math.abs(max.x) === Infinity || Math.abs(max.y) === Infinity || Math.abs(max.z) === Infinity
    );
    return result;
  }

// Group vectors by direction
const groupVectorsByDirection = (vectors: Vector3[]): Vector3[][] => {

    return vectors.reduce<Vector3[][]>((groups, vector) => {
        vector.normalize();
        const group = groups.find(g => vector.equals(g[0]));
        return group 
            ? (group.push(vector), groups) 
            : [...groups, [vector]];
    }, []);
}

const findOrientation = (products: Product[], scene : Scene) => {
    const walls = findFromProducts(products, [ElementTypeEnum.Wall]);

    const wallMeshes = findProductsFromSceneByGlobalIds(scene, walls.map(w => w.globalId));

    if (wallMeshes.length === 0) {
        return new Vector3(1,0,0); // Default direction
    }

    const vectorGroups = groupVectorsByDirection(wallMeshes.map(w => w.getWorldQuaternion(new Quaternion())).map(q => new Vector3(1,0,0).applyQuaternion(q)));

    const largestGroup = vectorGroups.reduce((max, group) => group.length > max.length ? group : max, []);

    return largestGroup[0];
}

const findAlignedBoundingBoxByProductType = (scene: Scene | null, rootProducts: Product[], types: ElementTypeEnum[], angle: number): OBB => {
    const alignedBox = new Box3();
    if (scene === null) return new OBB();

    const products = findFromProducts(rootProducts, types);
    const productObjects = findProductsFromSceneByGlobalIds(scene, products.map(p => p.globalId));
    const rotationMatrix = new Matrix4().makeRotationZ(-angle);
    const vertex = new Vector3();

    productObjects.forEach(obj => {
        obj.traverse((child) => {
            if (child instanceof Mesh && child.geometry) {
                const positionAttr = child.geometry.getAttribute('position') as BufferAttribute;
                if (positionAttr) {
                    const combinedMatrix = rotationMatrix.clone().multiply(child.matrixWorld);
                    for (let i = 0; i < positionAttr.count; i++) {
                        vertex.fromBufferAttribute(positionAttr, i);
                        vertex.applyMatrix4(combinedMatrix);
                        alignedBox.expandByPoint(vertex);
                    }
                }
            }
        });
    });

    if (alignedBox.isEmpty()) return new OBB();

    // Manually construct the OBB to avoid relying on OBB.fromBox3 / applyMatrix4 behaviour.
    // 1. Center: rotate the aligned-frame center back to world space (pure Z rotation).
    const alignedCenter = alignedBox.getCenter(new Vector3());
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const obb = new OBB();
    obb.center.set(
        alignedCenter.x * c - alignedCenter.y * s,
        alignedCenter.x * s + alignedCenter.y * c,
        alignedCenter.z
    );

    // 2. HalfSize: unchanged by a pure rotation.
    alignedBox.getSize(obb.halfSize).multiplyScalar(0.5);

    // 3. Rotation Matrix3 (column-major): columns are OBB local axes in world space.
    //    col1 = local X = ( cos, sin, 0 )
    //    col2 = local Y = ( -sin, cos, 0 )
    //    col3 = local Z = ( 0, 0, 1 )
    const el = obb.rotation.elements;
    el[0] = c;  el[3] = -s; el[6] = 0;
    el[1] = s;  el[4] =  c; el[7] = 0;
    el[2] = 0;  el[5] =  0; el[8] = 1;

    return obb;
};

const getModelBoundingBoxAndDirection = (scene: Scene, products: Product[], isCanvasHorizontal: boolean) => {

    const bbox = findBoundingBoxByProductType(scene, products, [ElementTypeEnum.Space]);

    if (bbox.isEmpty() || isBox3Infinite(bbox)){
        return {boundingBox: bbox, angle: 0};
    }

    // Match model aspect ratio to canvas aspect ratio
    const bboxSize = bbox.getSize(new Vector3());
    const baseAngle = bboxSize.x > bboxSize.y && isCanvasHorizontal 
                        ? 0
                        : Math.PI / 2;

    const referenceDir = new Vector3(1,0,0);

    // Rotate camera to align with walls
    const wallsDir = findOrientation(products, scene);

    // Step 1: Calculate the unsigned angle
    const angle = baseAngle + referenceDir.angleTo(wallsDir);

    // Step 2: Use cross product to determine the sign (CW or CCW)
    const cross = new Vector3().crossVectors(referenceDir, wallsDir);

    const angleWithDirection = cross.z < 0 ? -angle : angle;

    return {boundingBox: bbox, angle: angleWithDirection};
};

const areVectorsEqualWithinTolerance = (a: Vector3 | null | undefined, b: Vector3 | null | undefined, tolerance: number): boolean => {

    if (!a && !b ) return true; // Both are null or undefined
    if (!a || !b ) return false; // One is null/undefined, the other is not

    return (isEqualWithinTolerance(a.x, b.x, tolerance) &&
            isEqualWithinTolerance(a.y, b.y, tolerance) &&
            isEqualWithinTolerance(a.z, b.z, tolerance));
};


function getPerpendicularToLine(start: Vector3, end: Vector3, point: Vector3): Vector3 {
    const lineDir = end.clone().sub(start).normalize(); // Direction vector of the line
    const pointDir = point.clone().sub(start); // Vector from start to the given point
    const projectionLength = pointDir.dot(lineDir); // Projection of pointDir onto lineDir
    const projection = lineDir.multiplyScalar(projectionLength); // Projected vector on the line
    return pointDir.sub(projection); // Perpendicular vector from the line to the point
};

export { findBoundingBoxByProductType, findAlignedBoundingBoxByProductType, findOrientation, 
        findProductFromSceneByGlobalId, findProductsFromSceneByGlobalIds,
        getModelBoundingBoxAndDirection, 
        areVectorsEqualWithinTolerance, getPerpendicularToLine };
